import { Link } from '@tanstack/react-router';
import {
  buildDashboardDataTableColumns,
  DashboardDataTableColumnKind,
} from '@shigo-ui/dashboard-ui';
import { DashboardRevenueCapCell } from '../../DashboardRevenueCapCell/DashboardRevenueCapCell';
import { dashboardT } from '../../../../../lib/messages';
import { dashboardSrpm } from '../../../format';
import { formatCurrency, formatNumber, formatRatio } from '../../../../../lib/format';
import type { DashboardDateRange, DashboardDemandPartnerRow } from '../../../model';
import { dashboardTruncatedNameClassName, DashboardTruncatedName } from '../DashboardTruncatedName';
import { metricColumn, changeIndicator } from './dashboardMetricColumn';

export function partnerColumns(showComparison: boolean, range: DashboardDateRange) {
  return buildDashboardDataTableColumns<DashboardDemandPartnerRow>([
    {
      id: 'name',
      header: dashboardT('dashboard.table.partner'),
      kind: DashboardDataTableColumnKind.Custom,
      className: 'w-[220px] whitespace-nowrap',
      headerClassName: 'w-[220px]',
      cell: (row) => (
        <DashboardTruncatedName name={row.demandPartnerName}>
          {row.demandPartnerId ? (
            <Link
              className={`${dashboardTruncatedNameClassName} text-primary font-medium hover:underline`}
              to="/demand/partners/$demandPartnerId/details/overview"
              params={{ demandPartnerId: row.demandPartnerId }}
            >
              {row.demandPartnerName}
            </Link>
          ) : (
            <span className={dashboardTruncatedNameClassName}>{row.demandPartnerName}</span>
          )}
        </DashboardTruncatedName>
      ),
    },
    metricColumn(
      'dpAdRequests',
      'dashboard.table.bidRequests',
      (row) => row.dpAdRequests,
      (row) => row.dpAdRequestsChange,
      showComparison,
      formatNumber,
    ),
    metricColumn(
      'adImpressions',
      'dashboard.metric.adImpressions',
      (row) => row.adImpressions,
      (row) => row.adImpressionsChange,
      showComparison,
      formatNumber,
    ),
    metricColumn(
      'bidFillRatio',
      'dashboard.table.bidFillRatio',
      (row) => row.bidFillRatio,
      (row) => row.bidFillRatioChange,
      showComparison,
      formatRatio,
    ),
    metricColumn(
      'bids',
      'dashboard.table.bids',
      (row) => row.bids,
      (row) => row.bidsChange,
      showComparison,
      formatNumber,
    ),
    metricColumn(
      'bidRatio',
      'dashboard.table.bidRatio',
      (row) => row.bidRatio,
      (row) => row.bidRatioChange,
      showComparison,
      formatRatio,
    ),
    metricColumn(
      'winRatio',
      'dashboard.table.winRatio',
      (row) => row.winRatio,
      (row) => row.winRatioChange,
      showComparison,
      formatRatio,
    ),
    metricColumn(
      'marginPct',
      'dashboard.table.marginPct',
      (row) => row.marginPct,
      (row) => row.marginPctChange,
      showComparison,
      formatRatio,
    ),
    metricColumn(
      'wonBids',
      'dashboard.table.wonBids',
      (row) => row.wonBids,
      (row) => row.wonBidsChange,
      showComparison,
      formatNumber,
    ),
    metricColumn(
      'srpm',
      'dashboard.metric.srpm',
      (row) => row.srpm ?? dashboardSrpm(row.revenue, row.dpAdRequests),
      (row) => row.srpmChange,
      showComparison,
      formatCurrency,
    ),
    {
      id: 'revenueCap',
      header: dashboardT('dashboard.table.revenueCap'),
      kind: DashboardDataTableColumnKind.Custom,
      className: 'w-44',
      headerClassName: 'w-44',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          {row.revenueCapEnabled && row.revenueCap && row.revenueCap > 0 ? (
            <DashboardRevenueCapCell row={row} range={range} />
          ) : (
            formatCurrency(row.revenue)
          )}
          {showComparison ? changeIndicator(row.revenueChange) : null}
        </div>
      ),
    },
  ]);
}
